import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../../hooks/api";
import { useGlobal } from "../../hooks/global";
import { ITable } from "../../components/TableList";
import { IoAddCircleOutline } from "react-icons/io5";
import { PageList } from "../../components/PageList";
import { setUpConsultation } from "../../services/generalServices";
import { ModalUserType } from "../../components/Modals/ModalUserType";
import { IActions, IFilters, IUserTypesProps } from "../../interfaces/index";
import { openConfirmModal } from "@mantine/modals";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { IPermissionsTypes } from "../../utils/permissions";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { secondary } from "../../theme";

const initialFilters: IFilters[] = [];

const initialTable: ITable = {
  data: [],
  columns: [
    {
      attr: "name",
      name: "Nome",
      style: { width: "100%" },
      styleContent: { width: "100%", fontSize: 16, fontWeight: 600 },
    },
  ],
};

export const UserTypesList: React.FC = () => {
  const { user, api } = useApi();
  const { theme, paginationLimit, notify } = useGlobal();

  const [atualPage, setAtualPage] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [openModal, setOpenModal] = useState(false);
  const [numOfPersons, setNumOfPersons] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [searchString, setSearchString] = useState<string>("");
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [userSelected, setUserSelected] = useState<IUserTypesProps | null>(
    null
  );
  const [table, setTable] = useState<ITable>(initialTable);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const selectUserType = useCallback((userType: IUserTypesProps) => {
    setUserSelected({ ...userType });
    setOpenModal(true);
  }, []);

  const searchUserTypes = useCallback(
    async (page, _filters = null) => {
      setAtualPage(page);
      setLoadingPersons(true);
      try {
        const filters_obj = _filters
          ? _filters
          : { ...filters, search: searchString };

        let string = setUpConsultation(filters_obj);
        let url = `/user-types?page=${page}&limit=${paginationLimit}&${string}`;

        const result = await api.get(url);
        const new_users_types = result.data.data as IUserTypesProps[];
        setTable((atual) => {
          const new_table = { ...atual };
          new_table.data = [];
          new_users_types.forEach((userType) => {
            new_table.data.push({
              id: userType.id,
              name: userType.name,
              this: userType,
            });
          });
          return { ...new_table };
        });

        setNumOfPersons(result.data.count);
        setNumberOfPages(Math.ceil(result.data.count / paginationLimit));
        setUpdatedAt(new Date());
      } catch (err) {
        notify("Erro na busca pelos tipos de usuários!", "error");
      }
      setLoadingPersons(false);
    },
    [user, filters, searchString]
  );

  const openModalNewUserType = useCallback(() => {
    setUserSelected(null);
    setOpenModal(true);
  }, []);

  const handleAction = (action_id: string) => {
    if (action_id === "new_item") openModalNewUserType();
  };

  const actionsInitial: IActions[] = [
    {
      main_option: { text: "Novo Tipo de Usuário", id: "new_item" },
      className: "new_item",
      icon: <IoAddCircleOutline size={20} className="new_item" />,
      options: [],
      type: "solid",
      permissions: ["USER-TYPES-CREATE"],
    },
  ];

  const handleConfirm = useCallback(
    async (id: string) => {
      setLoadingDelete(true);
      try {
        await api.delete(`/user-types/${id}`);
        notify("Tipo de usuário excluído com sucesso!", "success");
        await searchUserTypes(1);
      } catch (error: any) {
        const default_message = "Erro ao excluir o tipo de usuário!";
        const message = error.response?.data?.detail ?? default_message;
        notify(message, message === default_message ? "error" : "alert");
      }
      setLoadingDelete(false);
    },
    [searchUserTypes]
  );

  const alertDelete = useCallback(
    async (type: IUserTypesProps): Promise<void> => {
      if (type?.is_adm) {
        window.alert(
          "Você não pode excluir um usuário " + type.name
        );

        return;
      }

      const result = window.confirm(
        "Tem certeza que deseja excluir o tipo " + type.name
      );

      if (result) handleConfirm(type.id as unknown as string);
    },
    [handleConfirm]
  );

  const onSaveUserType = useCallback(() => {
    setOpenModal(false);
    searchUserTypes(1);
  }, [user, filters, searchString]);

  const listAction = [
    {
      title: "Editar",
      onClick: (userType: IUserTypesProps) => selectUserType(userType),
      icon: <FaEdit color={secondary[theme]} size={20} />,
      disabled: () =>
        !user.user_type.permissions.includes(
          "USER-TYPES-UPDATE" as IPermissionsTypes
        ),
    },
    {
      title: "Excluir",
      onClick: (userType: IUserTypesProps) => alertDelete(userType),
      icon: <MdDelete color={secondary[theme]} size={20} />,
      disabled: () =>
        !user.user_type.permissions.includes(
          "USER-TYPES-DELETE" as IPermissionsTypes
        ),
    },
  ];

  useEffect(() => {
    searchUserTypes(1);
  }, []);

  return (
    <PageList
      updatedAt={updatedAt}
      loading={loadingPersons}
      setLoading={setLoadingPersons}
      numberOfItems={numOfPersons}
      actions={actionsInitial}
      onClickOption={handleAction}
      textHeader="Tipos de Usuários"
      textItems="tipos de usuários encontrados"
      text_loading={"Buscando Tipos de Usuários"}
      text_empty={"Nenhum tipo de usuário encontrado"}
      theme={theme}
      initialFilters={initialFilters}
      onChangeFilters={setFilters}
      table={table}
      listActions={listAction}
      pages={numberOfPages}
      page={atualPage}
      search={searchUserTypes}
      onChangeSearch={setSearchString}
    >
      {openModal && (
        <ModalUserType
          onCancel={() => setOpenModal(false)}
          onSave={onSaveUserType}
          openModal={openModal}
          backgroundVisible={false}
          defaultId={userSelected?.id}
        />
      )}
      {loadingDelete && <ModalLoading loading={loadingDelete} theme={theme} />}
    </PageList>
  );
};
